/**
 * walkthroughSteps.js
 *
 * Each step:
 *   id        - Unique identifier
 *   selector  - CSS selector for the spotlight target
 *   text      - Tooltip text (plain string)
 *   content   - Rich tooltip content (React element, overrides text)
 *   waitFor   - Selector to poll for before showing (defaults to selector)
 *   onEnter   - Runs when entering this step (navigate, expand menu, etc.)
 *   condition - If returns false, step is skipped
 *   skipAfter - Max ms to wait for target (default 6000)
 *   padding   - Extra px around spotlight (default 8)
 */
import React from 'react';
import { useSelector } from 'react-redux';

// Rendered inside the Remote Desktop walkthrough tooltip. The E4S launch
// recipe differs by cluster type:
//   - commissioned / bare metal: E4S ships as a Singularity image on /e4s
//   - cloud (IBM, AWS, etc.): E4S is installed via Spack on the image, no
//     Singularity layer, so the `singularity run` step is skipped.
function RemoteDesktopTerminalCommands() {
    var provider = useSelector(function (s) {
        return (s && s.clusters && s.clusters.cluster && s.clusters.cluster.cluster && s.clusters.cluster.cluster.provider) || '';
    });
    var isCloud = provider && provider !== 'commissioned' && provider !== 'maas' && provider !== 'local';
    return React.createElement('div', { style: { fontSize: 11, lineHeight: 1.8, fontFamily: 'monospace', backgroundColor: '#000', padding: '10px 12px', borderRadius: 6 } },
        React.createElement('div', { style: { color: '#cbd5e1', marginBottom: 6, fontFamily: 'inherit', fontSize: 10 } },
            isCloud ? 'Explore E4S from the terminal:' : 'Launch E4S from the terminal:'
        ),
        !isCloud && React.createElement('code', { style: { color: '#f59e0b', fontSize: 11, backgroundColor: 'transparent' } },
            'cd /e4s && singularity run --nv e4s-cuda120-aarch64-25.11.sif'
        ),
        !isCloud && React.createElement('br'),
        React.createElement('code', { style: { color: '#f59e0b', fontSize: 11, backgroundColor: 'transparent' } },
            'spack find -x'
        )
    );
}

export function buildWalkthroughSteps(opts) {
    var role = (opts && opts.role) || '';
    var steps = [];

    // ---- Welcome ----
    steps.push({
        id: 'welcome',
        selector: '.logo-icon',
        text: 'Welcome to Heidi AI! Let\'s take a quick tour of your workspace.',
        padding: 20
    });

    steps.push({
        id: 'nav-dashboard',
        selector: '[data-walkthrough="dashboard"]',
        text: 'This is your Dashboard - your home base for monitoring resources and quick actions.',
        padding: 6
    });

    // ---- Heidi Lab expanded menu tour ----
    steps.push({
        id: 'heidi-lab',
        selector: '[data-walkthrough="heidi-lab"]',
        text: 'Heidi Lab is where all your compute resources live.',
        onEnter: function (ctx) {
            // Open the folded navbar (simulates hover)
            ctx.dispatch({ type: 'OPEN_FOLDED_NAVBAR' });
            // Expand the Heidi Lab collapse group after sidebar animation
            setTimeout(function () {
                window.dispatchEvent(new CustomEvent('walkthrough-expand', { detail: 'heidi-lab' }));
            }, 300);
        },
        waitFor: '[data-walkthrough="credential-manager"]',
        skipAfter: 8000,
        padding: 6
    });

    steps.push({
        id: 'credential-manager',
        selector: '[data-walkthrough="credential-manager"]',
        text: 'Credentials Manager - store and manage cloud provider keys for deploying clusters on AWS, Azure, GCP, and more.',
        padding: 4
    });

    steps.push({
        id: 'stack-manager',
        selector: '[data-walkthrough="stack-manager"]',
        text: 'Stack Manager - define reusable cluster templates with OS, software, and configuration presets.',
        padding: 4
    });

    steps.push({
        id: 'cluster-manager-nav',
        selector: '[data-walkthrough="cluster-manager"]',
        text: 'Cluster Manager - deploy, monitor, and manage your HPC and AI clusters.',
        padding: 4
    });

    // ---- Cluster list ----
    steps.push({
        id: 'cluster-list',
        selector: '[data-walkthrough="cluster-row-0"]',
        text: 'Here are your clusters. You can see status, resources, and organization for each one.',
        onEnter: function (ctx) {
            // Close the folded navbar before navigating
            ctx.dispatch({ type: 'CLOSE_FOLDED_NAVBAR' });
            ctx.history.push('/clusters');
        },
        waitFor: '[data-walkthrough="cluster-row-0"]',
        padding: 12
    });

    // ---- Cluster detail ----
    steps.push({
        id: 'cluster-detail',
        selector: '#cluster_view_tab-applications',
        text: 'This is your cluster detail view - monitor resources, launch applications, and access your machine.',
        onEnter: function () {
            var row = document.querySelector('[data-walkthrough="cluster-row-0"]');
            if (row) {
                var nameCell = row.querySelector('td[id$="-name"]');
                if (nameCell) nameCell.click();
            }
        },
        waitFor: '#cluster_view_tab-applications',
        skipAfter: 8000,
        padding: 4
    });

    // ---- Applications tab ----
    steps.push({
        id: 'tab-applications',
        selector: '[data-walkthrough="cluster-app-0"]',
        content: React.createElement('div', null,
            React.createElement('div', { style: { marginBottom: 8 } },
                'Launch tools like Jupyter Lab, VS Code, RStudio, and more directly from the Applications tab - no command line needed.'
            ),
            React.createElement('img', {
                src: '/assets/images/logos/jupyter.svg',
                alt: 'Jupyter Lab',
                style: { width: 32, height: 32, marginRight: 8, verticalAlign: 'middle', display: 'inline-block' }
            }),
            React.createElement('span', { style: { fontSize: 13, color: '#94a3b8', verticalAlign: 'middle' } },
                'Jupyter Lab'
            ),
            React.createElement('div', { style: { marginTop: 10, fontSize: 12, color: '#94a3b8', lineHeight: 1.5 } },
                'Applications run in your browser with SSO - click once and you\'re in. ',
                'Unlike running notebooks inside a VNC desktop, launching Jupyter directly gives you native browser performance, GPU acceleration, and proper clipboard support.'
            )
        ),
        text: 'Launch tools like Jupyter Lab, VS Code, and more from the Applications tab.',
        onEnter: function () {
            var tab = document.querySelector('#cluster_view_tab-applications');
            if (tab) tab.click();
        },
        waitFor: '[data-walkthrough="cluster-app-0"]',
        condition: function () { return !!document.querySelector('#cluster_view_tab-applications'); },
        padding: 10
    });

    // ---- Access tab ----
    steps.push({
        id: 'tab-access',
        selector: '#cluster_view_tab-access',
        text: 'The Access tab provides remote desktop and SSH access to your cluster.',
        onEnter: function () {
            var tab = document.querySelector('#cluster_view_tab-access');
            if (tab) tab.click();
        },
        waitFor: '#cluster_view_tab-access',
        padding: 4
    });

    steps.push({
        id: 'remote-desktop',
        selector: '#cluster_view_remote_desktop',
        side: 'side', // tall rich content - prefer whichever horizontal side has more room
        content: React.createElement('div', null,
            React.createElement('div', { style: { marginBottom: 8 } },
                'Launch a full remote desktop session directly in your browser with more than 120 applications pre-installed - access the complete Linux desktop environment.'
            ),
            React.createElement('img', {
                src: '/assets/images/app-screenshots/e4s-1.png',
                alt: 'Remote desktop with E4S',
                style: { width: '100%', borderRadius: 8, border: '1px solid #334155', marginBottom: 8 }
            }),
            React.createElement(RemoteDesktopTerminalCommands)
        ),
        text: 'Launch a full remote desktop session directly in your browser.',
        waitFor: '#cluster_view_remote_desktop',
        skipAfter: 3000,
        padding: 12
    });

    // ---- Workshop flow (instructor/admin only) ----
    var isInstructor = role.indexOf('instructor') !== -1 || role.indexOf('admin') !== -1;

    if (isInstructor) {
        // Spotlight the workshops nav item
        steps.push({
            id: 'trainings-nav',
            selector: '[data-walkthrough="trainings"]',
            text: 'Heidi Workshops - organize hands-on lab sessions for your students or team. Share compute resources, track progress, and manage access from one place.',
            onEnter: function (ctx) {
                ctx.dispatch({ type: 'OPEN_FOLDED_NAVBAR' });
            },
            waitFor: '[data-walkthrough="trainings"]',
            skipAfter: 3000,
            padding: 6
        });

        // Navigate to workshops list
        steps.push({
            id: 'trainings-list',
            selector: '[data-walkthrough="training-row-0"]',
            text: 'Here are your workshops. Each session groups clusters and learners so everyone has the resources they need.',
            onEnter: function (ctx) {
                ctx.dispatch({ type: 'CLOSE_FOLDED_NAVBAR' });
                ctx.history.push('/trainings');
            },
            waitFor: '[data-walkthrough="training-row-0"]',
            skipAfter: 5000,
            padding: 12
        });

        // Click into the first workshop
        steps.push({
            id: 'training-detail',
            selector: '[data-walkthrough="training-clusters-table"]',
            text: 'Each workshop has one or more clusters attached. These are the compute resources your learners will use during the session.',
            onEnter: function () {
                var row = document.querySelector('[data-walkthrough="training-row-0"]');
                if (row) row.click();
            },
            waitFor: '[data-walkthrough="training-clusters-table"]',
            skipAfter: 5000,
            padding: 8
        });

        // Desktop icon (DeskCommand)
        steps.push({
            id: 'training-desktop',
            selector: '[data-walkthrough="training-desktop"]',
            text: 'DeskCommand - monitor all learner desktops in real-time from a single view. Great for live instruction, troubleshooting, and making sure everyone is on track.',
            waitFor: '[data-walkthrough="training-desktop"]',
            skipAfter: 2000,
            padding: 6
        });

        // Users icon
        steps.push({
            id: 'training-users',
            selector: '[data-walkthrough="training-users"]',
            text: 'View and manage participants - see who has joined, their assigned cluster, and connection status. Add or remove learners at any time.',
            waitFor: '[data-walkthrough="training-users"]',
            skipAfter: 2000,
            padding: 6
        });

        // Edit icon (pencil)
        steps.push({
            id: 'training-edit',
            selector: '[data-walkthrough="training-edit"]',
            text: 'Edit workshop settings - title, description, participant limits, cluster assignments, how learners are distributed across machines, and session scheduling.',
            waitFor: '[data-walkthrough="training-edit"]',
            skipAfter: 2000,
            padding: 6
        });

        // Start/Stop button - only start if inactive and cluster is deployed
        steps.push({
            id: 'training-start',
            selector: '[data-walkthrough="training-state-btn"]',
            text: 'Start the workshop to open it for learners. Once started, participants can join using the session code.',
            onEnter: function () {
                // Check if we should auto-start: workshop is inactive and cluster is deployed
                var stateBtn = document.querySelector('[data-walkthrough="training-state-btn"]');
                if (!stateBtn) return;
                var btn = stateBtn.querySelector('button');
                if (!btn) return;
                var btnText = btn.textContent || '';
                // Only start if button says "Start" (inactive workshop)
                if (btnText.toLowerCase().indexOf('start') === -1) return;
                // Check if any cluster in the table shows "available"/"deployed" state
                var table = document.querySelector('[data-walkthrough="training-clusters-table"]');
                if (!table) return;
                var hasDeployed = false;
                table.querySelectorAll('td').forEach(function (td) {
                    var text = (td.textContent || '').toLowerCase();
                    if (text === 'available' || text === 'deployed') hasDeployed = true;
                });
                if (hasDeployed) {
                    btn.click();
                }
            },
            waitFor: '[data-walkthrough="training-state-btn"]',
            skipAfter: 3000,
            padding: 6
        });

        // Wait for workshop to become active, then show share
        steps.push({
            id: 'training-share',
            selector: '[data-walkthrough="training-header-share"]',
            text: 'Share the session code with your class or team so they can join. Copy the code, share the link, or project it on screen during a live session.',
            waitFor: '[data-walkthrough="training-header-share"]',
            skipAfter: 30000,
            padding: 10
        });
    }

    // ---- Help menu (always shown near the end) ----
    steps.push({
        id: 'help-menu',
        selector: '[data-walkthrough="help-menu"]',
        text: 'If you have any questions or need help, click here at any time. You\'ll find the documentation, a link to contact our team, and an option to replay this tour.',
        waitFor: '[data-walkthrough="help-menu"]',
        side: 'left', // help button is top-right corner, tooltip fits better on the left
        skipAfter: 3000,
        padding: 6
    });

    // ---- End ----
    steps.push({
        id: 'tour-complete',
        selector: '.logo-icon',
        text: 'That\'s the tour! You\'re all set to start using Heidi AI. You can always replay this tour from the Help menu.',
        padding: 20
    });

    return steps;
}
